import { TIPI_ENTE, type TipoEnte } from "@app/schema";
import { DriveFolders, FolderExistsError, type FolderItem, type GraphClient, type TemplateNode } from "@app/sp-data";
import { codiceCompleto, codiceGara, eAlbo, loadLotti, nomeCartella, type Gara, type GareRepos, type Lotto, type Servizio } from "./gare";

/**
 * Creazione delle cartelle di gara: una per la gara, oppure una per ciascun lotto,
 * tutte nella stessa posizione, con la struttura del modello.
 *
 * Ripetibile: se si interrompe, rilanciandola si riprende nella stessa posizione,
 * si riusano le cartelle già create e si completano solo quelle mancanti.
 */

export interface CartelleCtx extends GareRepos {
  graph: GraphClient;
  siteId: string;
  /** Raccolta delle cartelle di gara. */
  repository: DriveFolders;
  /** URL della cartella modello (es. https://…/sites/Portale3/Modelli/CartellaGara). */
  modelloUrl: string;
}

export interface CartellaPrevista {
  nome: string;
  lotto?: Lotto;
  /** Già creata in un'esecuzione precedente. */
  fatta: boolean;
}

/** Perché le cartelle non si possono (ancora) creare; undefined = si può. */
export function impedimento(g: Gara, lotti: Lotto[], servizio?: Servizio): string | undefined {
  if (g.fields.CartellaId && !(g.fields.PerLotti && lotti.some((l) => !l.fields.CartellaId)))
    return "Le cartelle di questa gara sono già state create.";
  if (g.fields.Stato === "Archiviata") return "La gara è archiviata: per creare le cartelle va prima riattivata.";
  // Albi: la cartella si crea quando serve, senza valutazione
  if (!eAlbo(g) && g.fields.Valutazione !== "Partecipa") return 'Le cartelle si creano quando la valutazione è "Partecipa".';
  if (!g.fields.Codice) return "La gara non ha ancora un codice.";
  if (!servizio?.fields.Sigla) return "La gara non ha un servizio con sigla.";
  if (!eAlbo(g) && g.fields.PerLotti && !lotti.length) return "La gara è a lotti: aggiungi i lotti prima di creare le cartelle.";
  return undefined;
}

export function cartellePreviste(g: Gara, lotti: Lotto[], servizio: Servizio): CartellaPrevista[] {
  // Il codice completo è il nome della cartella; per i codici brevi delle prime prove lo si ricostruisce
  const codice = codiceCompleto(g.fields.Codice)
    ? g.fields.Codice!
    : codiceGara({ numero: g.fields.Numero ?? 0, sigla: servizio.fields.Sigla!, inserita: g.createdAt, albo: eAlbo(g) });
  if (!g.fields.PerLotti || eAlbo(g)) return [{ nome: nomeCartella(codice), fatta: false }];
  return lotti.map((l) => ({ nome: nomeCartella(codice, l.fields.Numero), lotto: l, fatta: !!l.fields.CartellaId }));
}

export async function leggiModello(ctx: CartelleCtx): Promise<TemplateNode[]> {
  const { folders, item } = await DriveFolders.fromUrl(ctx.graph, ctx.siteId, ctx.modelloUrl);
  const tree = await folders.readTemplate(item.id);
  if (!tree.length) throw new Error("Il modello non contiene cartelle: controllare l'indirizzo del modello nella configurazione.");
  return tree;
}

/**
 * Posizione già usata per questa gara: una creazione interrotta, o i lotti aggiunti
 * dopo, si completano lì, accanto alle altre cartelle.
 */
export async function posizioneSalvata(ctx: CartelleCtx, g: Gara): Promise<FolderItem | undefined> {
  if (!g.fields.PosizioneId) return undefined;
  return ctx.repository.get(g.fields.PosizioneId);
}

export type Evento =
  | { tipo: "modello" }
  | { tipo: "inizio"; nome: string; indice: number; totale: number }
  | { tipo: "avanzamento"; nome: string; fatte: number; totali: number }
  | { tipo: "fine"; nome: string; url: string; create: number; riusate: number };

export interface Esito {
  cartelle: Array<{ nome: string; url: string; id?: string; lotto?: number }>;
  contenitoreUrl: string;
}

export async function creaCartelle(
  ctx: CartelleCtx,
  g: Gara,
  servizio: Servizio,
  posizione: FolderItem,
  onEvento: (e: Evento) => void = () => undefined,
): Promise<Esito> {
  const lotti = g.fields.PerLotti ? await loadLotti(ctx, g.id) : [];
  const motivo = impedimento(g, lotti, servizio);
  if (motivo) throw new Error(motivo);
  if (g.fields.PosizioneId && g.fields.PosizioneId !== posizione.id)
    throw new Error("La creazione era già iniziata in un'altra posizione: va completata lì.");

  onEvento({ tipo: "modello" });
  const modello = await leggiModello(ctx);

  // La posizione si registra subito: un'interruzione si riprende nello stesso punto
  if (!g.fields.PosizioneId) await ctx.gare.update(g.id, { PosizioneId: posizione.id, Posizione: posizione.path || "/" });

  const previste = cartellePreviste(g, lotti, servizio);
  const esito: Esito = { cartelle: [], contenitoreUrl: posizione.webUrl };

  for (const [i, c] of previste.entries()) {
    const numeroLotto = c.lotto?.fields.Numero;
    if (c.fatta && c.lotto) {
      esito.cartelle.push({ nome: c.lotto.fields.NomeCartella ?? c.nome, url: c.lotto.fields.CartellaUrl ?? "", id: c.lotto.fields.CartellaId, lotto: numeroLotto });
      continue;
    }
    onEvento({ tipo: "inizio", nome: c.nome, indice: i, totale: previste.length });

    const res = await ctx.repository
      .buildTree({
        parentId: posizione.id,
        rootName: c.nome,
        template: modello,
        // Una cartella con lo stesso nome si riusa solo se è di questa gara (o è vuota di file)
        acceptExisting: async (ex) => {
          const f = await ctx.repository.fields<{ GaraLookupId?: string | number; Lotto?: number }>(ex.id);
          if (f.GaraLookupId != null && f.GaraLookupId !== "") return Number(f.GaraLookupId) === g.id && (f.Lotto ?? undefined) === numeroLotto;
          return (await ctx.repository.countDeep(ex.id)).files === 0;
        },
        onProgress: (p) => onEvento({ tipo: "avanzamento", nome: c.nome, fatte: p.done, totali: p.total }),
      })
      .catch((e: unknown) => {
        if (e instanceof FolderExistsError)
          throw new Error(`In questa posizione esiste già "${e.folderName}" e contiene documenti di un'altra gara o non collegati: verificare in SharePoint.`);
        throw e;
      });

    await ctx.repository.setFields(res.root.id, { GaraLookupId: String(g.id), Lotto: numeroLotto ?? null });
    if (c.lotto) await ctx.lotti.update(c.lotto.id, { CartellaId: res.root.id, CartellaUrl: res.root.webUrl, NomeCartella: c.nome });
    esito.cartelle.push({ nome: c.nome, url: res.root.webUrl, id: res.root.id, lotto: numeroLotto });
    onEvento({ tipo: "fine", nome: c.nome, url: res.root.webUrl, create: res.created, riusate: res.reused });
  }

  // Tutto creato: la gara riceve il link (la sua cartella, o quella che contiene i lotti)
  // e, se manca, il tipo di ente ricavato dal primo livello della posizione
  const tipo = g.fields.TipoEnte ? undefined : tipoEnteDaPercorso(posizione.path);
  if (g.fields.PerLotti) {
    await ctx.gare.update(g.id, { CartellaId: posizione.id, CartellaUrl: posizione.webUrl, NomeCartella: null, ...(tipo ? { TipoEnte: tipo } : {}) });
  } else {
    const unica = esito.cartelle[0]!;
    await ctx.gare.update(g.id, { CartellaId: unica.id, CartellaUrl: unica.url, NomeCartella: unica.nome, ...(tipo ? { TipoEnte: tipo } : {}) });
  }
  return esito;
}

/** "Comuni/A/Alatri" → "Comuni" (se il primo livello è un tipo di ente noto). */
export function tipoEnteDaPercorso(path: string): TipoEnte | undefined {
  const primo = path.split("/")[0]?.trim().toLowerCase();
  return TIPI_ENTE.find((t) => t.toLowerCase() === primo);
}

/** Cartelle della raccolta che sono cartelle di gara (non navigabili né usabili come destinazione). */
export function eCartellaDiGara(f: FolderItem, modello?: TemplateNode[], figli?: FolderItem[]): boolean {
  if (/^OA\d{4}([_ -]|$)/i.test(f.name)) return true;
  // Contiene le sottocartelle del modello: è un fascicolo, qualunque sia il nome
  if (modello && figli) {
    const nomi = new Set(figli.map((x) => x.name.toLowerCase()));
    const uguali = modello.filter((m) => nomi.has(m.name.toLowerCase())).length;
    return uguali >= Math.min(3, modello.length);
  }
  return false;
}
